// ============================================================
// White-label client portal — rendered outside the dashboard chrome.
//   /portal/:slug/*  on the dashboard's own hosts
//   /*               on an agency's custom domain (resolved by host)
// ============================================================

import { useEffect, useMemo, type ReactNode } from 'react'
import { Route, Routes, useParams } from 'react-router-dom'
import { Box, CircularProgress, CssBaseline, ThemeProvider, Typography } from '@mui/material'

import { useResolvePortalQuery, type PortalLookup } from '@/apis/portalApi'
import type { PortalPublic } from '@/apis/portalTypes'

import { PortalContext } from './portalContext'
import { createPortalTheme } from './portalTheme'
import {
    PortalAcceptPage,
    PortalForgotPage,
    PortalLoginPage,
    PortalResetPage,
} from './PortalAuthPages'
import { PortalReportsView } from './PortalReportsView'

export function PortalBySlug() {
    const { slug = '' } = useParams()
    const normalized = slug.toLowerCase()
    return <PortalRoot lookup={{ slug: normalized }} basePath={`/portal/${normalized}`} />
}

export function PortalByHost({ host }: { host: string }) {
    return (
        <Routes>
            <Route path="/*" element={<PortalRoot lookup={{ host }} basePath="" />} />
        </Routes>
    )
}

function Centered({ children }: { children: ReactNode }) {
    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 3,
                textAlign: 'center',
            }}
        >
            {children}
        </Box>
    )
}

function PortalRoot({ lookup, basePath }: { lookup: PortalLookup; basePath: string }) {
    const { data: portal, isLoading, isError } = useResolvePortalQuery(lookup)

    if (isLoading) {
        return (
            <Centered>
                <CircularProgress size={28} />
            </Centered>
        )
    }
    if (isError || !portal) {
        return (
            <Centered>
                <Box>
                    <Typography variant="h5" sx={{ mb: 1 }}>
                        This page isn't available
                    </Typography>
                    <Typography color="text.secondary">
                        The reporting portal you're looking for doesn't exist or has been switched
                        off. Check the link you were sent.
                    </Typography>
                </Box>
            </Centered>
        )
    }
    return <BrandedPortal portal={portal} basePath={basePath} />
}

function BrandedPortal({ portal, basePath }: { portal: PortalPublic; basePath: string }) {
    const theme = useMemo(() => createPortalTheme(portal.branding), [portal.branding])
    const ctx = useMemo(() => ({ portal, basePath }), [portal, basePath])

    useEffect(() => {
        const previous = document.title
        document.title = portal.branding.productName
        return () => {
            document.title = previous
        }
    }, [portal.branding.productName])

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <PortalContext.Provider value={ctx}>
                <Routes>
                    <Route path="login" element={<PortalLoginPage />} />
                    <Route path="forgot" element={<PortalForgotPage />} />
                    <Route path="accept" element={<PortalAcceptPage />} />
                    <Route path="reset" element={<PortalResetPage />} />
                    <Route path="*" element={<PortalReportsView />} />
                </Routes>
            </PortalContext.Provider>
        </ThemeProvider>
    )
}
